import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Lock, LogOut, Mail, Phone, Calendar, Users, Package, MessageSquare,
  Inbox, Utensils, RefreshCw, AlertTriangle, ChevronDown, Check, RotateCcw, Search, FileText, Globe, UsersRound, Code2,
} from "lucide-react";
import { api } from "../lib/api";
import { business } from "../data/site";
import SeoEditor from "./SeoEditor";
import UsersAdmin from "./UsersAdmin";
import TrackingAdmin from "./TrackingAdmin";

const TOKEN_KEY = "shaan_admin_token";
const setAuthHeader = (token) => {
  if (token) api.defaults.headers.common.Authorization = `Bearer ${token}`;
  else delete api.defaults.headers.common.Authorization;
};

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" });

// ---------- Login ----------
function LoginCard({ onLogin }) {
  const [form, setForm] = useState({ username: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/login", form);
      localStorage.setItem(TOKEN_KEY, data.token);
      setAuthHeader(data.token);
      toast.success(`Welcome ${data.user.full_name || data.user.username}`);
      onLogin(data.user);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[85vh] flex items-center justify-center px-5 py-24">
      <div className="w-full max-w-md bg-[#141414] border border-white/10 rounded-2xl p-8 md:p-10">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 grid place-items-center rounded-full bg-[#ffd100]/10">
            <Lock size={20} className="text-[#ffd100]" />
          </div>
          <p className="text-[#ffd100] font-semibold uppercase tracking-[0.2em] text-xs">Owner Inbox</p>
        </div>
        <h1 className="font-display text-4xl font-bold text-white leading-tight">Admin Sign in</h1>
        <p className="text-neutral-400 text-base mt-2">Access catering requests and contact messages.</p>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
          <div>
            <label className="block text-sm font-semibold text-neutral-300 mb-2 uppercase tracking-wider">Username</label>
            <input
              data-testid="admin-username-input"
              value={form.username}
              onChange={update("username")}
              autoComplete="username"
              placeholder="owner"
              className="w-full bg-[#0a0a0a] border border-white/20 rounded-xl px-5 py-4 text-white text-lg focus:border-[#ffd100] outline-none placeholder:text-neutral-600"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-neutral-300 mb-2 uppercase tracking-wider">Password</label>
            <input
              data-testid="admin-password-input"
              type="password"
              value={form.password}
              onChange={update("password")}
              autoComplete="current-password"
              placeholder="••••••••"
              className="w-full bg-[#0a0a0a] border border-white/20 rounded-xl px-5 py-4 text-white text-lg focus:border-[#ffd100] outline-none placeholder:text-neutral-600"
            />
          </div>
          <button
            data-testid="admin-login-submit"
            disabled={submitting}
            className="w-full bg-[#ffd100] text-[#0a0a0a] font-bold text-lg py-4 rounded-full hover:bg-[#e5bc00] transition-colors disabled:opacity-60"
          >
            {submitting ? "Signing in…" : "Sign in"}
          </button>
          <p className="text-neutral-500 text-sm text-center pt-2">Need help? Call {business.phone}</p>
        </form>
      </div>
    </div>
  );
}

// ---------- Row (compact) ----------
function InquiryRow({ item, kind, onToggleStatus }) {
  const [open, setOpen] = useState(false);
  const isCatering = kind === "catering";
  const completed = item.status === "completed";
  const preview = isCatering
    ? [item.package, item.event_date && `📅 ${item.event_date}`, item.guests && `${item.guests} guests`].filter(Boolean).join(" · ")
    : item.subject || (item.message?.slice(0, 60) + (item.message && item.message.length > 60 ? "…" : ""));

  return (
    <div
      data-testid={`row-${kind}-${item.id}`}
      className={`bg-[#141414] border rounded-xl overflow-hidden transition-colors ${
        completed ? "border-white/5 opacity-70" : "border-white/10 hover:border-[#ffd100]/40"
      }`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left px-5 py-4 flex items-start gap-4"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`font-display text-lg font-semibold ${completed ? "text-neutral-400 line-through" : "text-white"}`}>
              {item.name}
            </span>
            {!completed && (
              <span className="text-[10px] font-bold uppercase tracking-wider bg-[#ffd100] text-[#0a0a0a] px-2 py-0.5 rounded-full">New</span>
            )}
          </div>
          <p className={`text-sm mt-1 truncate ${completed ? "text-neutral-500" : "text-neutral-400"}`}>
            {preview || "—"}
          </p>
        </div>
        <div className="hidden md:flex flex-col items-end shrink-0 gap-1">
          <span className="text-xs text-neutral-500">{fmtDate(item.created_at)}</span>
        </div>
        <ChevronDown
          size={18}
          className={`text-neutral-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="border-t border-white/10 px-5 py-5 bg-[#0f0f0f]">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-base">
            <a href={`mailto:${item.email}`} className="flex items-center gap-2 text-neutral-200 hover:text-[#ffd100]">
              <Mail size={16} className="text-[#ffd100]" /> {item.email}
            </a>
            {item.phone && (
              <a href={`tel:${item.phone}`} className="flex items-center gap-2 text-neutral-200 hover:text-[#ffd100]">
                <Phone size={16} className="text-[#ffd100]" /> {item.phone}
              </a>
            )}
            {isCatering && item.event_date && (
              <span className="flex items-center gap-2 text-neutral-200"><Calendar size={16} className="text-[#ffd100]" /> {item.event_date}</span>
            )}
            {isCatering && item.guests && (
              <span className="flex items-center gap-2 text-neutral-200"><Users size={16} className="text-[#ffd100]" /> {item.guests} guests</span>
            )}
            {isCatering && item.package && (
              <span className="flex items-center gap-2 text-neutral-200"><Package size={16} className="text-[#ffd100]" /> {item.package}</span>
            )}
            {!isCatering && item.subject && (
              <span className="flex items-center gap-2 text-neutral-200"><MessageSquare size={16} className="text-[#ffd100]" /> {item.subject}</span>
            )}
          </div>
          {item.message && (
            <p className="mt-4 text-neutral-300 text-base leading-relaxed whitespace-pre-wrap border-t border-white/10 pt-4">
              {item.message}
            </p>
          )}
          <div className="mt-5 flex flex-wrap gap-3 md:hidden">
            <span className="text-xs text-neutral-500">Received {fmtDate(item.created_at)}</span>
          </div>
          <div className="mt-5 flex flex-wrap gap-3">
            {completed ? (
              <button
                data-testid={`reopen-${item.id}`}
                onClick={(e) => { e.stopPropagation(); onToggleStatus(item, "new"); }}
                className="inline-flex items-center gap-2 border-2 border-white/20 text-white font-semibold px-4 py-2 rounded-full hover:border-[#ffd100] hover:text-[#ffd100] transition-colors text-sm"
              >
                <RotateCcw size={14} /> Reopen
              </button>
            ) : (
              <button
                data-testid={`complete-${item.id}`}
                onClick={(e) => { e.stopPropagation(); onToggleStatus(item, "completed"); }}
                className="inline-flex items-center gap-2 bg-[#ffd100] text-[#0a0a0a] font-bold px-4 py-2 rounded-full hover:bg-[#e5bc00] transition-colors text-sm"
              >
                <Check size={14} /> Mark as complete
              </button>
            )}
            <a
              href={`mailto:${item.email}`}
              className="inline-flex items-center gap-2 border-2 border-white/20 text-white font-semibold px-4 py-2 rounded-full hover:border-[#ffd100] hover:text-[#ffd100] transition-colors text-sm"
            >
              <Mail size={14} /> Reply
            </a>
            {item.phone && (
              <a
                href={`tel:${item.phone}`}
                className="inline-flex items-center gap-2 border-2 border-white/20 text-white font-semibold px-4 py-2 rounded-full hover:border-[#ffd100] hover:text-[#ffd100] transition-colors text-sm"
              >
                <Phone size={14} /> Call
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function FailedEmailRow({ item }) {
  return (
    <div className="bg-[#141414] border border-red-500/30 rounded-xl px-5 py-4 flex items-start gap-4">
      <AlertTriangle size={20} className="text-red-300 shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1">
        <p className="text-white font-semibold">{item.name || "Guest"} <span className="text-neutral-500 font-normal text-sm">· {item.kind}</span></p>
        <a href={`mailto:${item.recipient}`} className="text-neutral-300 text-sm hover:text-[#ffd100] break-all">{item.recipient}</a>
        <p className="text-red-300/80 text-xs mt-1">Not delivered: {item.reason}</p>
      </div>
      <span className="text-xs text-neutral-500 shrink-0 hidden md:block">{fmtDate(item.created_at)}</span>
    </div>
  );
}

// ---------- Email preview panel ----------
const TEMPLATES = [
  { id: "customer-catering", label: "Guest: Catering Confirmation", audience: "Customer", desc: "Sent to a guest right after they submit the catering form." },
  { id: "customer-contact", label: "Guest: Contact Confirmation", audience: "Customer", desc: "Sent to a guest right after they submit the contact form." },
  { id: "owner-catering", label: "Owner: New Catering Request", audience: "Owner", desc: "Sent to shaancurryhouse@gmail.com when a catering request arrives (guest confirmed)." },
  { id: "owner-catering-failed", label: "Owner: New Catering (guest email NOT delivered)", audience: "Owner", desc: "Same as above, but with the red banner shown when the guest confirmation failed to deliver." },
  { id: "owner-contact", label: "Owner: New Contact Message", audience: "Owner", desc: "Sent to shaancurryhouse@gmail.com when a contact form is submitted." },
];

function TemplatesPanel() {
  const [active, setActive] = useState(TEMPLATES[0].id);
  const token = localStorage.getItem(TOKEN_KEY);
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const src = `${backend}/api/admin/email-preview/${active}?token=${encodeURIComponent(token || "")}`;
  const meta = TEMPLATES.find((t) => t.id === active);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-5">
      <aside className="bg-[#141414] border border-white/10 rounded-2xl p-3 h-fit">
        <p className="px-3 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-neutral-500">Preview a template</p>
        {TEMPLATES.map((t) => (
          <button
            key={t.id}
            data-testid={`tpl-${t.id}`}
            onClick={() => setActive(t.id)}
            className={`w-full text-left px-3 py-3 rounded-xl transition-colors ${
              active === t.id ? "bg-[#ffd100] text-[#0a0a0a]" : "text-neutral-200 hover:bg-white/5"
            }`}
          >
            <p className="text-sm font-semibold leading-snug">{t.label}</p>
            <p className={`text-xs mt-1 ${active === t.id ? "text-[#0a0a0a]/70" : "text-neutral-500"}`}>{t.audience}</p>
          </button>
        ))}
      </aside>
      <div className="min-w-0">
        <div className="bg-[#141414] border border-white/10 rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-white/10 flex items-start justify-between gap-3 flex-wrap">
            <div>
              <p className="text-[#ffd100] text-xs font-bold uppercase tracking-[0.16em]">{meta?.audience} Email</p>
              <p className="font-display text-xl text-white mt-1">{meta?.label}</p>
              <p className="text-neutral-400 text-sm mt-1">{meta?.desc}</p>
            </div>
            <a
              href={src}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 border-2 border-white/20 text-white font-semibold px-4 py-2 rounded-full hover:border-[#ffd100] hover:text-[#ffd100] transition-colors text-sm shrink-0"
            >
              Open in new tab
            </a>
          </div>
          <div className="bg-[#f4f1ea] p-4">
            <iframe
              key={active}
              data-testid="tpl-frame"
              title="email-preview"
              src={src}
              className="w-full h-[780px] bg-white rounded-lg shadow"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Toolbar buttons ----------
const MainTab = ({ id, icon: Icon, label, count, activeId, onSelect }) => (
  <button
    data-testid={`tab-${id}-btn`}
    onClick={() => onSelect(id)}
    className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-base font-semibold border-2 transition-colors ${
      activeId === id
        ? "bg-[#ffd100] text-[#0a0a0a] border-[#ffd100]"
        : "bg-transparent text-[#ffd100] border-[#ffd100]/50 hover:bg-[#ffd100] hover:text-[#0a0a0a]"
    }`}
  >
    <Icon size={16} /> {label} <span className="opacity-70">({count})</span>
  </button>
);

const StatusPill = ({ value, label, count, activeStatus, onSelect }) => (
  <button
    data-testid={`status-${value}-btn`}
    onClick={() => onSelect(value)}
    className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
      activeStatus === value
        ? "bg-white text-[#0a0a0a]"
        : "bg-transparent text-neutral-400 hover:text-white"
    }`}
  >
    {label} <span className="opacity-60">({count})</span>
  </button>
);

// ---------- Dashboard ----------
function Dashboard({ user, onLogout }) {
  const can = (perm) => user?.role === "owner" || user?.permissions?.includes(perm);
  const isOwner = user?.role === "owner";
  const defaultTab = can("leads") ? "catering" : can("seo") ? "seo" : can("tracking") ? "tracking" : isOwner ? "users" : "catering";
  const [tab, setTab] = useState(defaultTab); // catering | contact | failed | templates | seo | tracking | users
  const [status, setStatus] = useState("new"); // new | completed
  const [query, setQuery] = useState("");
  const [catering, setCatering] = useState([]);
  const [contact, setContact] = useState([]);
  const [failed, setFailed] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      if (can("leads")) {
        const [c, m, f] = await Promise.all([
          api.get("/admin/catering"),
          api.get("/admin/contact"),
          api.get("/admin/failed-emails"),
        ]);
        setCatering(c.data);
        setContact(m.data);
        setFailed(f.data);
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        onLogout();
      } else {
        toast.error("Failed to load inbox");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateStatus = async (item, newStatus) => {
    const isCatering = tab === "catering";
    const path = isCatering ? `/admin/catering/${item.id}/status` : `/admin/contact/${item.id}/status`;
    const setter = isCatering ? setCatering : setContact;
    // Optimistic update
    setter((list) => list.map((x) => (x.id === item.id ? { ...x, status: newStatus } : x)));
    try {
      await api.patch(path, { status: newStatus });
      toast.success(newStatus === "completed" ? "Marked as complete" : "Reopened");
    } catch (err) {
      toast.error("Update failed");
      setter((list) => list.map((x) => (x.id === item.id ? { ...x, status: item.status } : x)));
    }
  };

  const source = tab === "catering" ? catering : tab === "contact" ? contact : failed;
  const counts = useMemo(() => {
    const active = tab === "catering" ? catering : contact;
    return {
      catNew: catering.filter((x) => (x.status || "new") === "new").length,
      catDone: catering.filter((x) => x.status === "completed").length,
      conNew: contact.filter((x) => (x.status || "new") === "new").length,
      conDone: contact.filter((x) => x.status === "completed").length,
      activeNew: active.filter((x) => (x.status || "new") === "new").length,
      activeDone: active.filter((x) => x.status === "completed").length,
    };
  }, [catering, contact, tab]);

  const list = useMemo(() => {
    if (tab === "failed") return source;
    const filtered = source.filter((x) => (x.status || "new") === status);
    if (!query.trim()) return filtered;
    const q = query.trim().toLowerCase();
    return filtered.filter((x) =>
      [x.name, x.email, x.phone, x.subject, x.message, x.package]
        .filter(Boolean)
        .some((s) => s.toLowerCase().includes(q))
    );
  }, [source, tab, status, query]);

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-10 py-24 md:py-28">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
        <div>
          <p className="text-[#ffd100] font-semibold uppercase tracking-[0.2em] text-sm mb-2">Owner Inbox</p>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-white tracking-tight">Submissions</h1>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="admin-refresh-btn"
            onClick={load}
            className="inline-flex items-center gap-2 border-2 border-white/20 text-white font-semibold px-5 py-2.5 rounded-full hover:border-[#ffd100] hover:text-[#ffd100] transition-colors"
          >
            <RefreshCw size={16} /> Refresh
          </button>
          <button
            data-testid="admin-logout-btn"
            onClick={onLogout}
            className="inline-flex items-center gap-2 border-2 border-white/20 text-white font-semibold px-5 py-2.5 rounded-full hover:border-[#ffd100] hover:text-[#ffd100] transition-colors"
          >
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </div>

      {/* Main tabs */}
      <div className="flex gap-3 mb-6 flex-wrap">
        {can("leads") && <MainTab id="catering" icon={Utensils} label="Catering" count={counts.catNew} activeId={tab} onSelect={setTab} />}
        {can("leads") && <MainTab id="contact" icon={MessageSquare} label="Contact" count={counts.conNew} activeId={tab} onSelect={setTab} />}
        {can("leads") && (
          <button
            data-testid="tab-failed-btn"
            onClick={() => setTab("failed")}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-base font-semibold border-2 transition-colors ${
              tab === "failed"
                ? "bg-red-500 text-white border-red-500"
                : failed.length > 0
                  ? "bg-transparent text-red-300 border-red-500/60 hover:bg-red-500 hover:text-white hover:border-red-500"
                  : "bg-transparent text-neutral-500 border-white/15 hover:border-red-500/60 hover:text-red-300"
            }`}
          >
            <AlertTriangle size={16} /> Needs Follow-up ({failed.length})
          </button>
        )}
        {can("leads") && (
          <button
            data-testid="tab-templates-btn"
            onClick={() => setTab("templates")}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-base font-semibold border-2 transition-colors ${
              tab === "templates"
                ? "bg-white text-[#0a0a0a] border-white"
                : "bg-transparent text-neutral-300 border-white/25 hover:border-white hover:text-white"
            }`}
          >
            <FileText size={16} /> Email Templates
          </button>
        )}
        {can("seo") && (
          <button
            data-testid="tab-seo-btn"
            onClick={() => setTab("seo")}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-base font-semibold border-2 transition-colors ${
              tab === "seo"
                ? "bg-white text-[#0a0a0a] border-white"
                : "bg-transparent text-neutral-300 border-white/25 hover:border-white hover:text-white"
            }`}
          >
            <Globe size={16} /> SEO
          </button>
        )}
        {can("tracking") && (
          <button
            data-testid="tab-tracking-btn"
            onClick={() => setTab("tracking")}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-base font-semibold border-2 transition-colors ${
              tab === "tracking"
                ? "bg-white text-[#0a0a0a] border-white"
                : "bg-transparent text-neutral-300 border-white/25 hover:border-white hover:text-white"
            }`}
          >
            <Code2 size={16} /> Tracking
          </button>
        )}
        {isOwner && (
          <button
            data-testid="tab-users-btn"
            onClick={() => setTab("users")}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-base font-semibold border-2 transition-colors ${
              tab === "users"
                ? "bg-white text-[#0a0a0a] border-white"
                : "bg-transparent text-neutral-300 border-white/25 hover:border-white hover:text-white"
            }`}
          >
            <UsersRound size={16} /> Users
          </button>
        )}
      </div>

      {/* Status + search toolbar (hidden for non-lead tabs) */}
      {(tab === "catering" || tab === "contact") && (
        <div className="bg-[#141414] border border-white/10 rounded-full p-1.5 flex items-center gap-1 flex-wrap mb-6">
          <StatusPill value="new" label="New" count={counts.activeNew} activeStatus={status} onSelect={setStatus} />
          <StatusPill value="completed" label="Completed" count={counts.activeDone} activeStatus={status} onSelect={setStatus} />
          <div className="flex-1 min-w-[180px] relative ml-auto">
            <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input
              data-testid="admin-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, email, phone…"
              className="w-full bg-transparent text-white text-sm placeholder:text-neutral-500 px-4 py-2 pl-10 outline-none"
            />
          </div>
        </div>
      )}

      {tab === "templates" ? (
        <TemplatesPanel />
      ) : tab === "seo" ? (
        <SeoEditor />
      ) : tab === "tracking" ? (
        <TrackingAdmin />
      ) : tab === "users" ? (
        <UsersAdmin />
      ) : loading ? (
        <p className="text-neutral-400 text-lg">Loading…</p>
      ) : list.length === 0 ? (
        <div className="bg-[#141414] border border-white/10 rounded-2xl p-12 text-center">
          <div className="w-14 h-14 grid place-items-center rounded-full bg-[#ffd100]/10 mx-auto mb-4">
            <Inbox size={26} className="text-[#ffd100]" />
          </div>
          <h3 className="font-display text-2xl font-semibold text-white">
            {tab === "failed"
              ? "All caught up"
              : status === "new"
                ? `No new ${tab} requests`
                : `No completed ${tab} yet`}
          </h3>
          <p className="text-neutral-400 mt-2">
            {tab === "failed"
              ? "Every guest has received their confirmation email."
              : status === "new"
                ? "New submissions will appear here as they arrive."
                : "Items you mark as complete will move here."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2.5" data-testid="admin-list">
          {tab === "failed"
            ? list.map((i, idx) => <FailedEmailRow key={`${i.recipient}-${idx}`} item={i} />)
            : list.map((i) => (
                <InquiryRow key={i.id} item={i} kind={tab} onToggleStatus={updateStatus} />
              ))}
        </div>
      )}
    </div>
  );
}

// ---------- Page ----------
export default function Admin() {
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) {
      setReady(true);
      return;
    }
    setAuthHeader(t);
    api.get("/admin/me")
      .then(({ data }) => setUser(data))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setAuthHeader(null);
      })
      .finally(() => setReady(true));
  }, []);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthHeader(null);
    setUser(null);
  };

  if (!ready) {
    return <div className="max-w-3xl mx-auto px-5 py-40 text-neutral-400 text-lg">Loading…</div>;
  }

  return (
    <div data-testid="admin-page">
      {user ? <Dashboard user={user} onLogout={logout} /> : <LoginCard onLogin={setUser} />}
    </div>
  );
}
